import React, { useState, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import Modal from './Modal';
import ConfirmDialog from './ConfirmDialog';
import { useActionToast } from './ActionToast';

// Manage document categories — rename them, change what they DO, add new ones
// with a behaviour model (shown / required / internal), and remove a category.
//
// BEHAVIOUR IS EDITABLE (Rob, 2026-08-14: "within categories I think we also
// need a setting to say whether they go through the risk flag" — Environmental
// was pulling through the risk flag and shouldn't have been). It used to be
// fixed at creation, so the only way to correct a category was SQL.
//
// `behaviour = 'required'` IS the risk-flag setting: only required categories
// appear in the risk matrix (RiskFlagMatrix filters on it) and only they can
// make a supplier non-compliant. So the fix is to let it change, not to add a
// second flag beside it that could disagree.
//
// Changing it moves suppliers between compliant and not, so it asks first and
// says which way — see CONSEQUENCE below. That is the whole reason this is a
// confirm rather than a dropdown that writes on change.
//
// Delete is guarded: a category can only be removed once it holds NO live
// documents. "Live" means anything that isn't archived (active / draft /
// superseded). Archive the documents first and the category becomes
// removable — archived rows stay in the Archived list regardless. New
// categories appear immediately in the upload picker.

const BEHAVIOURS = [
  { key: 'shown',    label: 'Shown',    hint: 'Shown to suppliers (per-document toggle); can carry a recognition award.' },
  { key: 'required', label: 'Required', hint: 'Becomes a required compliance document; which suppliers see it is set by risk tier.' },
  { key: 'internal', label: 'Internal', hint: 'Admin-only; never shown to suppliers.' },
];
const BADGE = {
  shown:    'text-fusion-forest bg-[#92D400]/10 border-[#92D400]/30',
  required: 'text-[#2E8FA1] bg-[#2E8FA1]/10 border-[#2E8FA1]/30',
  internal: 'text-[#51534A] bg-[#51534A]/10 border-[#DADCCD]/30',
};

// What actually happens to suppliers when a category changes behaviour. Stated
// in terms of compliance, because that is the part with teeth: moving OUT of
// `required` can turn a blocked supplier green, and moving IN can turn a green
// supplier red without anyone touching that supplier.
const CONSEQUENCE = {
  required: {
    to: 'Documents in it become required compliance documents, and which suppliers see them is set by the risk tier matrix.',
    warn: 'Suppliers missing them will go non-compliant, which blocks them from new work.',
  },
  shown: {
    to: 'Documents in it stop being compliance requirements — suppliers can still see and complete them, and the category can carry a recognition award.',
    warn: 'Any supplier currently red or blocked only because of these documents will go green.',
  },
  internal: {
    to: 'Documents in it become admin-only and are never shown to suppliers.',
    warn: 'Suppliers lose sight of these documents entirely, including any they had part-completed.',
  },
};

const slugify = (s) => (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60);
const inputCls = "bg-white border border-line rounded-lg text-ink px-3 py-2 font-['Metropolis'] text-sm focus:outline-none focus:border-[#92D400] transition-colors";

export default function ManageCategoriesPanel({ open, onClose, categories = [], badgeCategories = [], docs = [], onSaved }) {
  const [labels, setLabels] = useState({}); // key → edited label (dirty)
  const [newName, setNewName] = useState('');
  const [newBehaviour, setNewBehaviour] = useState('shown');
  const [error, setError] = useState(null);
  const { notify, Toast } = useActionToast();
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // category pending deletion
  const [confirmBehaviour, setConfirmBehaviour] = useState(null); // { cat, next }

  // Live (non-archived) document count per category key. A category is only
  // removable when this is 0 — archived documents don't block.
  const liveCountByKey = useMemo(() => {
    const out = {};
    for (const d of docs) {
      if (d.status === 'archived') continue;
      const k = d.document_type || 'internal_reference';
      out[k] = (out[k] || 0) + 1;
    }
    return out;
  }, [docs]);

  if (!open) return null;

  const rename = async (cat) => {
    const next = (labels[cat.key] ?? cat.label).trim();
    if (!next || next === cat.label) { setLabels(p => { const c = { ...p }; delete c[cat.key]; return c; }); return; }
    setBusy(true); setError(null);
    const { error: e } = await supabase.from('document_categories').update({ label: next }).eq('key', cat.key);
    setBusy(false);
    if (e) { setError(e.message); return; }
    setLabels(p => { const c = { ...p }; delete c[cat.key]; return c; });
    notify({ title: `Renamed to “${next}”`, tone: 'success' });
    onSaved?.();
  };

  const add = async () => {
    const name = newName.trim();
    if (!name) { setError('Give the category a name.'); return; }
    const key = slugify(name);
    if (!key) { setError('That name has no usable letters or numbers.'); return; }
    if (categories.some(c => c.key === key)) { setError('A category with a similar name already exists.'); return; }
    setBusy(true); setError(null);
    const sort = Math.max(0, ...categories.map(c => c.sort_order || 0)) + 1;
    const { error: e } = await supabase.from('document_categories').insert([{ key, label: name, behaviour: newBehaviour, sort_order: sort }]);
    setBusy(false);
    if (e) { setError(e.message); return; }
    setNewName(''); setNewBehaviour('shown');
    notify({ title: `Category “${name}” added`, tone: 'success' });
    onSaved?.();
  };

  const changeBehaviour = async () => {
    const pending = confirmBehaviour;
    setConfirmBehaviour(null);
    if (!pending) return;
    const { cat, next } = pending;
    setBusy(true); setError(null);
    const patch = { behaviour: next };
    // A recognition award only belongs on a 'shown' category (the DB keeps
    // badge_category_id and badge_tier_awarded co-present). Moving away from
    // 'shown' therefore has to clear the award in the same write, or the row
    // ends up carrying a badge nothing will ever grant.
    if (next !== 'shown' && cat.badge_category_id) {
      patch.badge_category_id = null;
      patch.badge_tier_awarded = null;
    }
    const { error: e } = await supabase.from('document_categories').update(patch).eq('key', cat.key);
    setBusy(false);
    if (e) { setError(e.message); return; }
    notify({
      eyebrow: 'Category changed',
      title: `“${cat.label}” is now ${next}`,
      body: patch.badge_category_id === null ? 'Its recognition award was removed — only shown categories can carry one.' : undefined,
      tone: 'success',
    });
    onSaved?.();
  };

  const remove = async () => {
    const cat = confirmDel;
    setConfirmDel(null);
    if (!cat) return;
    // Re-check the guard at commit time in case docs changed under us.
    if ((liveCountByKey[cat.key] || 0) > 0) { setError('That category still holds live documents.'); return; }
    setBusy(true); setError(null);
    const { error: e } = await supabase.from('document_categories').delete().eq('key', cat.key);
    setBusy(false);
    if (e) { setError(e.message); return; }
    notify({ title: `Category “${cat.label}” removed`, tone: 'success' });
    onSaved?.();
  };

  // Move a category up/down by swapping its sort_order with the neighbour in
  // that direction. categories arrives already ordered by sort_order, so the
  // neighbour is just the adjacent array entry. Two writes; reload reflects it.
  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= categories.length) return;
    const a = categories[index];
    const b = categories[target];
    setBusy(true); setError(null);
    // Guard against equal/duplicate sort_order values (older seed data) so the
    // swap always produces a strict reordering.
    const aOrder = a.sort_order ?? index;
    const bOrder = b.sort_order === a.sort_order ? aOrder + dir : (b.sort_order ?? target);
    const [{ error: e1 }, { error: e2 }] = await Promise.all([
      supabase.from('document_categories').update({ sort_order: bOrder }).eq('key', a.key),
      supabase.from('document_categories').update({ sort_order: aOrder }).eq('key', b.key),
    ]);
    setBusy(false);
    if (e1 || e2) { setError((e1 || e2).message); return; }
    onSaved?.();
  };

  // Set (or clear) a category's recognition badge. Only 'shown' categories are
  // eligible. Writing the category keeps badge_category_id + badge_tier_awarded
  // co-present (DB constraint); tier defaults to 'bronze' — inert for eco, which
  // grows Acorn→Oak from the category link alone. Then re-stamp every live doc
  // in the category so the "documents inherit automatically" contract holds for
  // existing docs, not just newly-uploaded ones.
  // Toggle whether an EMPTY 'shown' category shows a "Coming soon" placeholder
  // in the supplier portal (vs being hidden). Categories with documents always
  // show regardless.
  const setCategoryComingSoon = async (cat, next) => {
    setBusy(true); setError(null);
    const { error: e } = await supabase
      .from('document_categories')
      .update({ portal_coming_soon: next })
      .eq('key', cat.key);
    setBusy(false);
    if (e) { setError(e.message); return; }
    notify({ title: next ? 'Showing “Coming soon” to suppliers' : 'Coming-soon placeholder hidden', tone: 'success' });
    onSaved?.();
  };

  const setCategoryBadge = async (cat, badgeCategoryId) => {
    setBusy(true); setError(null);
    const tier = badgeCategoryId ? 'bronze' : null;
    const { error: e } = await supabase
      .from('document_categories')
      .update({ badge_category_id: badgeCategoryId || null, badge_tier_awarded: tier })
      .eq('key', cat.key);
    if (e) { setBusy(false); setError(e.message); return; }
    // Re-link live (non-archived) documents in this category.
    const { error: relErr } = await supabase
      .from('fusion_documents')
      .update({ badge_category_id: badgeCategoryId || null, badge_tier_awarded: tier })
      .eq('document_type', cat.key)
      .neq('status', 'archived');
    setBusy(false);
    if (relErr) { setError(relErr.message); return; }
    notify({ title: badgeCategoryId ? 'Recognition badge linked' : 'Recognition badge removed', tone: 'success' });
    onSaved?.();
  };

  return (
    <Modal open onClose={onClose} maxWidth={560} ariaLabel="Manage categories">
      <Modal.Header eyebrow="Fusion Documents" title="Manage categories" sub="Rename a category, change what it does, add new ones, or remove an empty one." onClose={onClose} />
      <Modal.Body>
        <div className="space-y-2.5 mb-6">
          {categories.map((cat, index) => {
            const val = labels[cat.key] ?? cat.label;
            const dirty = labels[cat.key] != null && labels[cat.key].trim() !== cat.label;
            const liveCount = liveCountByKey[cat.key] || 0;
            const locked = liveCount > 0;
            const awardable = cat.behaviour === 'shown';
            return (
              <div key={cat.key} className="border border-line rounded-lg p-2.5 space-y-2">
                <div className="flex items-center gap-2.5">
                  {/* Reorder arrows */}
                  <div className="flex flex-col flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => move(index, -1)}
                      disabled={busy || index === 0}
                      aria-label={`Move ${cat.label} up`}
                      className="bg-transparent border-0 p-0 leading-none text-[#51534A] hover:text-ink cursor-pointer disabled:opacity-20 disabled:cursor-default">
                      <ChevronIcon dir="up" />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(index, 1)}
                      disabled={busy || index === categories.length - 1}
                      aria-label={`Move ${cat.label} down`}
                      className="bg-transparent border-0 p-0 leading-none text-[#51534A] hover:text-ink cursor-pointer disabled:opacity-20 disabled:cursor-default">
                      <ChevronIcon dir="down" />
                    </button>
                  </div>
                  <input
                    className={`${inputCls} flex-1`}
                    value={val}
                    onChange={e => setLabels(p => ({ ...p, [cat.key]: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') rename(cat); }} />
                  {/* Was a read-only badge. It's now the control, because
                      "does this category go through the risk flag" is exactly
                      what behaviour decides and there was no way to change it
                      short of SQL. Styled as the badge still, so the row reads
                      the same at a glance. */}
                  <select
                    value={cat.behaviour}
                    disabled={busy}
                    onChange={e => {
                      const next = e.target.value;
                      if (next !== cat.behaviour) setConfirmBehaviour({ cat, next });
                    }}
                    aria-label={`What ${cat.label} does`}
                    title={BEHAVIOURS.find(b => b.key === cat.behaviour)?.hint}
                    className={`font-['Metropolis'] text-[9px] font-bold tracking-[1.5px] uppercase rounded-md pl-2 pr-6 py-1 border whitespace-nowrap cursor-pointer appearance-none bg-no-repeat disabled:opacity-50 ${BADGE[cat.behaviour] || BADGE.internal}`}
                    style={{
                      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='5' viewBox='0 0 8 5'%3E%3Cpath d='M0 0l4 5 4-5z' fill='%2351534A'/%3E%3C/svg%3E\")",
                      backgroundPosition: 'right 6px center',
                    }}>
                    {BEHAVIOURS.map(b => <option key={b.key} value={b.key}>{b.key}</option>)}
                  </select>
                  <button
                    type="button"
                    onClick={() => rename(cat)}
                    disabled={busy || !dirty}
                    className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.12em] text-fusion-forest hover:text-fusion-forest bg-transparent border-0 cursor-pointer disabled:opacity-30 disabled:cursor-default">
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => locked ? null : setConfirmDel(cat)}
                    disabled={busy || locked}
                    title={locked
                      ? `Can't remove — ${liveCount} live document${liveCount === 1 ? '' : 's'} in this category. Archive ${liveCount === 1 ? 'it' : 'them'} first.`
                      : `Remove ${cat.label}`}
                    aria-label={locked ? `${cat.label} can't be removed — has live documents` : `Remove ${cat.label}`}
                    className={`bg-transparent border-0 p-1 flex-shrink-0 transition-colors ${locked
                      ? 'text-[#3a3b36] cursor-not-allowed'
                      : 'text-[#51534A] hover:text-[#E0290A] cursor-pointer'}`}>
                    <TrashIcon />
                  </button>
                </div>

                {/* Recognition badge — shown categories only. Every document in
                    the category inherits this award automatically. */}
                {awardable && (
                  <div className="flex items-center gap-2 pl-[26px]">
                    <span className="font-['Metropolis'] text-[10px] font-bold tracking-[0.15em] uppercase text-[#51534A] whitespace-nowrap">Recognition award</span>
                    <select
                      className={`${inputCls} py-1.5 flex-1 max-w-[280px]`}
                      value={cat.badge_category_id || ''}
                      disabled={busy}
                      onChange={e => setCategoryBadge(cat, e.target.value || null)}>
                      <option value="">None</option>
                      {badgeCategories.map(b => (
                        <option key={b.id} value={b.id}>{b.name}{b.slug === 'eco' ? ' (Acorn → Oak Status)' : ''}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Coming-soon placeholder — shown categories only. When on, an
                    EMPTY category shows a "Coming soon" teaser in the supplier
                    portal instead of being hidden. */}
                {awardable && (
                  <label className="flex items-center gap-2 pl-[26px] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!cat.portal_coming_soon}
                      disabled={busy}
                      onChange={e => setCategoryComingSoon(cat, e.target.checked)}
                      className="accent-[#92D400]" />
                    <span className="font-['Metropolis'] text-[11px] text-fusion-mid-grey">
                      Show a <strong className="text-ink">“Coming soon”</strong> placeholder in the supplier portal while this category is empty
                    </span>
                  </label>
                )}
              </div>
            );
          })}
        </div>

        {/* Add new */}
        <div className="border-t border-line pt-5">
          <p className="font-['Metropolis'] text-[11px] font-bold tracking-[0.15em] uppercase text-[#51534A] mb-2.5">Add a category</p>
          <div className="flex items-center gap-2.5 flex-wrap">
            <input className={`${inputCls} flex-1 min-w-[160px]`} placeholder="Category name" value={newName} onChange={e => setNewName(e.target.value)} />
            <select className={inputCls} value={newBehaviour} onChange={e => setNewBehaviour(e.target.value)}>
              {BEHAVIOURS.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
            </select>
            <button type="button" onClick={add} disabled={busy}
              className="bg-[#92D400] text-[#131313] rounded-xl font-['Metropolis'] text-xs font-bold px-4 py-2 hover:bg-[#a4e810] transition-colors disabled:opacity-50">Add</button>
          </div>
          <p className="font-['Metropolis'] text-[11px] text-[#51534A] mt-2">{BEHAVIOURS.find(b => b.key === newBehaviour)?.hint}</p>
        </div>

        {error && <p className="font-['Metropolis'] text-sm text-[#E0290A] mt-3">{error}</p>}
      </Modal.Body>
      <Modal.Footer>
        <div className="flex justify-end">
          <button type="button" onClick={onClose}
            className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.15em] text-[#51534A] hover:text-fusion-mid-grey bg-transparent border-0 cursor-pointer">Done</button>
        </div>
      </Modal.Footer>

      <ConfirmDialog
        open={!!confirmDel}
        title={confirmDel ? `Remove "${confirmDel.label}"?` : ''}
        body="This removes the category from the upload picker and the document library. It has no live documents, so nothing suppliers can see is affected. Archived documents stay in the Archived list. This can't be undone."
        confirmLabel="Remove category"
        cancelLabel="Cancel"
        tone="danger"
        onConfirm={remove}
        onCancel={() => setConfirmDel(null)} />
      {/* Changing behaviour moves suppliers between compliant and not, without
          anyone touching a supplier. So the dialog names the direction and how
          many live documents ride on it, rather than asking "are you sure?" */}
      <ConfirmDialog
        open={!!confirmBehaviour}
        title={confirmBehaviour ? `Make “${confirmBehaviour.cat.label}” ${confirmBehaviour.next}?` : ''}
        body={confirmBehaviour ? [
          CONSEQUENCE[confirmBehaviour.next]?.to,
          (liveCountByKey[confirmBehaviour.cat.key] || 0) > 0
            ? `${liveCountByKey[confirmBehaviour.cat.key]} live document${liveCountByKey[confirmBehaviour.cat.key] === 1 ? '' : 's'} sit in this category. ${CONSEQUENCE[confirmBehaviour.next]?.warn}`
            : 'Nothing live sits in this category yet, so no supplier changes today.',
          confirmBehaviour.next !== 'shown' && confirmBehaviour.cat.badge_category_id
            ? 'Its recognition award will be removed — only shown categories can carry one.'
            : null,
        ].filter(Boolean).join(' ') : ''}
        confirmLabel={confirmBehaviour ? `Make it ${confirmBehaviour.next}` : 'Confirm'}
        cancelLabel="Leave as is"
        tone={confirmBehaviour && (liveCountByKey[confirmBehaviour.cat.key] || 0) > 0 ? 'danger' : 'primary'}
        onConfirm={changeBehaviour}
        onCancel={() => setConfirmBehaviour(null)} />
      <Toast />
    </Modal>
  );
}

const ChevronIcon = ({ dir }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
    style={{ transform: dir === 'down' ? 'rotate(180deg)' : 'none' }}>
    <polyline points="18 15 12 9 6 15" />
  </svg>
);

const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);
